import React, { useEffect, useState, useCallback, useRef } from "react";
import moment from "moment";
import { IProductionDashboardStepDTO } from "../../models/DTO/Production/IProductionResponseDTO";
import * as ProductionService from "../../services/ProductionService";
import { ProductionAuthContext } from "../../services/ProductionService";
import IOwTableCols from "../../models/IOwTableCols";
import UserTypesEnum from "../../models/UserTypesEnum";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import PaginationBar from "../shared/PaginationBar/PaginationBar";
import SerialWorkflowHistory from "./SerialWorkflowHistory";
import MoveSerialsModal from "./MoveSerialsModal";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";

interface ProductionStepDrilldownProps {
    step: IProductionDashboardStepDTO;
    allSteps: IProductionDashboardStepDTO[];
    ctx: ProductionAuthContext;
    onBack: () => void;
}

const DEFAULT_PAGE_SIZE = 25;

const ProductionStepDrilldown: React.FC<ProductionStepDrilldownProps> = ({ step, allSteps, ctx, onBack }) => {
    const [serials, setSerials] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialData, setIsInitialData] = useState(true);
    const [historySerialId, setHistorySerialId] = useState<number | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [moveMode, setMoveMode] = useState<"step" | "inventory" | null>(null);
    const [isMoving, setIsMoving] = useState(false);
    const [moveError, setMoveError] = useState<string | null>(null);

    const userTypeId = ctx.userModel?.supplierUserToken?.User_Type?.id;
    const isAdmin = userTypeId === UserTypesEnum.ADMIN || userTypeId === UserTypesEnum.MANAGEMENT;
    const terminalStep = allSteps.find((s) => s.is_terminal && s.stepId != null) ?? null;

    // Superseded requests (rapid paging) must not overwrite the current page's rows.
    const fetchIdRef = useRef(0);

    const fetchSerials = useCallback(async () => {
        const fetchId = ++fetchIdRef.current;
        setIsLoading(true);
        try {
            const data = await ProductionService.getSerialsByStep(ctx, step.stepId ?? 0, page, pageSize);
            if (fetchId !== fetchIdRef.current) return;
            setSerials(data.serials);
            setTotalCount(data.totalCount);
        } catch (err) {
            console.error("Failed to fetch step serials", err);
        } finally {
            if (fetchId === fetchIdRef.current) {
                setIsLoading(false);
                setIsInitialData(false);
            }
        }
    }, [step.stepId, page, pageSize]);

    useEffect(() => {
        fetchSerials();
    }, [fetchSerials]);

    // Selections are page-scoped: reset when the visible rows change.
    useEffect(() => {
        setSelectedIds(new Set());
    }, [step.stepId, page, pageSize]);

    const toggleSerial = (serialId: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(serialId)) {
                next.delete(serialId);
            } else {
                next.add(serialId);
            }
            return next;
        });
    };

    const allOnPageSelected = serials.length > 0 && serials.every((s) => selectedIds.has(s.id));

    const toggleSelectPage = () => {
        if (allOnPageSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(serials.map((s) => s.id)));
        }
    };

    const handleMoveConfirm = async (targetStepId: number, notes: string) => {
        setIsMoving(true);
        setMoveError(null);
        try {
            const result = await ProductionService.advanceSerials(ctx, {
                serial_ids: [...selectedIds],
                serial_workflow_step_id: targetStepId,
                notes: notes || undefined,
                is_manual_override: true,
            });

            if (result.errors?.length) {
                setMoveError(result.errors.join(", "));
                return;
            }

            setMoveMode(null);
            setSelectedIds(new Set());
            await fetchSerials();
        } catch (err: any) {
            console.error("Failed to move serials", err);
            setMoveError(err?.message || "Failed to move serials");
        } finally {
            setIsMoving(false);
        }
    };

    const columns: IOwTableCols[] = [
        ...(isAdmin
            ? [
                  {
                      title: "",
                      valueProp: "id",
                      renderFn: (row: any) => (
                          <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedIds.has(row.id)}
                              onChange={() => toggleSerial(row.id)}
                              onClick={(e) => e.stopPropagation()}
                          />
                      ),
                      renderFnEmitsRow: true,
                  } as IOwTableCols,
              ]
            : []),
        {
            title: "serial",
            valueProp: "value",
            renderFn: (row: any) => {
                return (
                    <span className="fw-bold text-primary" style={{ cursor: "pointer" }} onClick={() => setHistorySerialId(row.id)}>
                        {row.value}
                    </span>
                );
            },
            renderFnEmitsRow: true,
        },
        {
            title: "model",
            valueProp: "Catalog_Item",
            renderFn: (row: any) => row.Catalog_Item?.name || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "caliber",
            valueProp: "Catalog_Item",
            renderFn: (row: any) => row.Catalog_Item?.caliber || "—",
            renderFnEmitsRow: true,
        },
        {
            title: "time_at_step",
            valueProp: "current_step_entered_at",
            renderFn: (row: any) => {
                const enteredAt = row.current_step_entered_at || row.updated_at;
                return enteredAt ? moment(enteredAt).fromNow(true) : "—";
            },
            renderFnEmitsRow: true,
        },
        {
            title: "last_log",
            valueProp: "Last_Log",
            renderFn: (row: any) => {
                if (!row.Last_Log) return "—";
                return (
                    <>
                        {row.Last_Log.Employee_User?.name && <span className="me-2">{row.Last_Log.Employee_User.name}</span>}
                        {row.Last_Log.completed_at && (
                            <small className="text-muted">{moment(row.Last_Log.completed_at).format("MM/DD hh:mm A")}</small>
                        )}
                    </>
                );
            },
            renderFnEmitsRow: true,
        },
    ];

    return (
        <div className="pd-drilldown">
            <div className="pd-drilldown__header">
                <button className="btn btn-outline-secondary btn-sm" onClick={onBack}>
                    <i className="fas fa-arrow-left me-1" /> Back
                </button>
                <h4 className="pd-drilldown__title mb-0 ms-3">
                    <OwTranslate id={step.stepName} defaultMessage={step.stepName} />
                    <span className="text-muted ms-2" style={{ fontSize: "0.875rem" }}>
                        ({step.serialCount} serials)
                    </span>
                </h4>
            </div>

            {isAdmin && (
                <div className="pd-drilldown__action-bar">
                    <button className="btn btn-outline-secondary btn-sm" onClick={toggleSelectPage} disabled={serials.length === 0}>
                        {allOnPageSelected ? "Clear Selection" : "Select Page"}
                    </button>
                    <span className="pd-drilldown__selected-count text-muted">{selectedIds.size} selected</span>
                    <button
                        className="btn btn-warning btn-sm"
                        disabled={selectedIds.size === 0}
                        onClick={() => {
                            setMoveError(null);
                            setMoveMode("step");
                        }}>
                        <i className="fas fa-arrows-turn-to-dots me-1" />
                        Move to Step... ({selectedIds.size})
                    </button>
                    <button
                        className="btn btn-warning btn-sm"
                        disabled={selectedIds.size === 0 || !terminalStep || step.stepId === terminalStep.stepId}
                        onClick={() => {
                            setMoveError(null);
                            setMoveMode("inventory");
                        }}>
                        <i className="fas fa-boxes-stacked me-1" />
                        Move to Inventory ({selectedIds.size})
                    </button>
                </div>
            )}

            <OwFnTable
                columns={columns}
                dataSet={serials}
                dataFetch={isLoading}
                initialData={isInitialData}
                tableConfig={{ tableHover: true }}
                rowKeyProp="id"
            />

            {totalCount > 0 && (
                <PaginationBar
                    offset={page}
                    limit={pageSize}
                    totalRecords={totalCount}
                    onPaginationChanged={(newPage: number, newLimit: number) => {
                        setPage(newPage);
                        setPageSize(newLimit);
                    }}
                />
            )}

            <SerialWorkflowHistory
                show={historySerialId !== null}
                serialId={historySerialId}
                ctx={ctx}
                onClose={() => setHistorySerialId(null)}
            />

            {moveMode && (
                <MoveSerialsModal
                    show={moveMode !== null}
                    mode={moveMode}
                    selectedCount={selectedIds.size}
                    currentStep={step}
                    steps={allSteps}
                    terminalStep={terminalStep}
                    isMoving={isMoving}
                    errorMessage={moveError}
                    onConfirm={handleMoveConfirm}
                    onClose={() => setMoveMode(null)}
                />
            )}
        </div>
    );
};

export default ProductionStepDrilldown;
